import { z } from "zod";

/**
 * Schemas Zod alineados con `api/app/schemas/tickets.py`.
 * v1.2.0 — F11/F112.
 */

// ── Enums (deben matchear los Literal[] del backend) ───────────────────
export const ticketCategoriaEnum = [
  "soporte_tecnico",
  "facturacion",
  "instalacion",
  "cancelacion",
  "otro",
] as const;

export const ticketPrioridadEnum = ["baja", "normal", "alta", "urgente"] as const;
export const ticketEstadoEnum = ["abierto", "en_proceso", "resuelto", "cerrado"] as const;

export type TicketCategoria = (typeof ticketCategoriaEnum)[number];
export type TicketPrioridad = (typeof ticketPrioridadEnum)[number];
export type TicketEstado = (typeof ticketEstadoEnum)[number];

// Etiquetas en español para UI
export const TICKET_CATEGORIA_LABEL: Record<TicketCategoria, string> = {
  soporte_tecnico: "Soporte técnico",
  facturacion: "Facturación",
  instalacion: "Instalación",
  cancelacion: "Cancelación",
  otro: "Otro",
};

export const TICKET_PRIORIDAD_LABEL: Record<TicketPrioridad, string> = {
  baja: "Baja",
  normal: "Normal",
  alta: "Alta",
  urgente: "Urgente",
};

export const TICKET_ESTADO_LABEL: Record<TicketEstado, string> = {
  abierto: "Abierto",
  en_proceso: "En proceso",
  resuelto: "Resuelto",
  cerrado: "Cerrado",
};

// ── Embeds ─────────────────────────────────────────────────────────────
export type ClienteEmbed = {
  id: string;
  codigo: string;
  razon_social: string;
  nombre_comercial: string | null;
};

export type UserEmbed = {
  id: string;
  nombre: string;
  email: string;
};

// ── Adjuntos ───────────────────────────────────────────────────────────
export type AdjuntoOut = {
  id: string;
  filename: string;
  mime: string;
  size_bytes: number;
  subido_por_cliente: boolean;
  creado_at: string;
};

// ── Comentarios ────────────────────────────────────────────────────────
export const comentarioCreateSchema = z.object({
  cuerpo: z.string().min(1, "Escribe un comentario").max(10_000),
  interno: z.boolean().default(false),
});
export type ComentarioCreate = z.infer<typeof comentarioCreateSchema>;

export type ComentarioOut = {
  id: string;
  ticket_id: string;
  autor_es_cliente: boolean;
  autor: UserEmbed | null;
  cuerpo: string;
  interno: boolean;
  creado_at: string;
  adjuntos: AdjuntoOut[];
};

// ── Ticket — admin ─────────────────────────────────────────────────────
export const ticketCreateSchema = z.object({
  cliente_id: z.string().uuid("Selecciona un cliente"),
  categoria: z.enum(ticketCategoriaEnum),
  prioridad: z.enum(ticketPrioridadEnum).default("normal"),
  asunto: z.string().min(3, "Mínimo 3 caracteres").max(200),
  descripcion: z.string().min(3, "Describe el problema").max(10_000),
  asignado_a: z.string().uuid().nullable().optional(),
});
export type TicketCreate = z.infer<typeof ticketCreateSchema>;

export const ticketUpdateSchema = z.object({
  categoria: z.enum(ticketCategoriaEnum).optional(),
  prioridad: z.enum(ticketPrioridadEnum).optional(),
  asunto: z.string().min(3).max(200).optional(),
});
export type TicketUpdate = z.infer<typeof ticketUpdateSchema>;

export const ticketStateChangeSchema = z.object({
  estado: z.enum(ticketEstadoEnum),
  motivo: z.string().max(500).optional(),
});
export type TicketStateChange = z.infer<typeof ticketStateChangeSchema>;

export type TicketSummary = {
  id: string;
  codigo: string;
  cliente: ClienteEmbed;
  categoria: TicketCategoria;
  prioridad: TicketPrioridad;
  estado: TicketEstado;
  asunto: string;
  asignado: UserEmbed | null;
  creado_at: string;
  ultimo_mensaje_at: string;
  creado_por_cliente: boolean;
};

export type TicketDetail = {
  id: string;
  codigo: string;
  cliente: ClienteEmbed;
  categoria: TicketCategoria;
  prioridad: TicketPrioridad;
  estado: TicketEstado;
  asunto: string;
  descripcion: string;
  creador: UserEmbed | null;
  creado_por_cliente: boolean;
  asignado: UserEmbed | null;
  asignado_at: string | null;
  creado_at: string;
  actualizado_at: string;
  ultimo_mensaje_at: string;
  resuelto_at: string | null;
  resuelto_por: UserEmbed | null;
  cerrado_at: string | null;
  cerrado_por: UserEmbed | null;
  comentarios: ComentarioOut[];
  adjuntos_iniciales: AdjuntoOut[];
};

export type TicketsListResponse = {
  items: TicketSummary[];
  total: number;
  pagina: number;
  por_pagina: number;
};

export type TicketCounters = {
  abiertos_asignados_a_mi: number;
  abiertos_total: number;
  en_proceso_total: number;
  sin_asignar: number;
};

// ── Filtros de listado ─────────────────────────────────────────────────
export type EstadoGrupo = "abiertos" | "finales" | "todos";

export type TicketsFilters = {
  page?: number;
  per_page?: number;
  estado?: TicketEstado;
  estado_grupo?: EstadoGrupo;
  categoria?: TicketCategoria;
  prioridad?: TicketPrioridad;
  asignado_a?: string;
  asignados_a_mi?: boolean;
  sin_asignar?: boolean;
  cliente_id?: string;
  busqueda?: string;
};
